import React, { useState, useEffect } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import { fetchAnalyticsOperators } from "./analyticsApi";
import type { AnalyticsOperatorRow } from "../../types/analytics";

interface OperatorsTabProps {
  filters: AnalyticsFilters;
}

type SortKey = "ordersCreated" | "conversionRate" | "reachRate" | "reached" | "attempts" | "leadsTaken";

const OperatorsTab: React.FC<OperatorsTabProps> = ({ filters }) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState<AnalyticsOperatorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [sortBy, setSortBy] = useState<SortKey>("ordersCreated");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAnalyticsOperators(filters, page, limit, sortBy, order)
      .then((res) => {
        if (!cancelled) {
          setRows(res.rows);
          setTotal(res.total);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.task, filters.operatorId, page, limit, sortBy, order, t]);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else setSortBy(key);
  };

  const SortIcon = ({ column }: { column: SortKey }) =>
    sortBy === column ? order === "asc" ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" /> : <ArrowUpDown className="w-4 h-4 text-gray-400" />;

  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.operator")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("leadsTaken")} className="flex items-center gap-1">
                    {t("analytics.leadsTaken")} <SortIcon column="leadsTaken" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.leadsCompletedCol")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("attempts")} className="flex items-center gap-1">
                    {t("analytics.attempts")} <SortIcon column="attempts" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("reached")} className="flex items-center gap-1">
                    {t("analytics.reached")} <SortIcon column="reached" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("ordersCreated")} className="flex items-center gap-1">
                    {t("analytics.ordersCreated")} <SortIcon column="ordersCreated" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("reachRate")} className="flex items-center gap-1">
                    {t("analytics.reachRate")} <SortIcon column="reachRate" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("conversionRate")} className="flex items-center gap-1">
                    {t("analytics.conversionRate")} <SortIcon column="conversionRate" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.callbacksScheduled")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.overdueCallbacks")}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => (
                <tr key={row.operatorId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.username || row.operatorId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.leadsTaken}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.leadsCompleted}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.attempts}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.reached}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.ordersCreated}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.reachRate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.conversionRate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.callbacksScheduled}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.overdueCallbacks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && total > limit && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <span>
            {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 text-[#264a3e] hover:bg-[#264a3e]/5"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page * limit >= total}
              className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 text-[#264a3e] hover:bg-[#264a3e]/5"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorsTab;
